import { mutate } from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';

// Must match `getKey` in useOrganizationConversations. Bound
// `mutateConversations` only updates the currently mounted SWR infinite
// key (All vs Unread vs Needs review), so sibling filters stay stale
// until this global revalidate.
const ORGANIZATION_CONVERSATIONS_SWR_KEY = 'organization-conversations';

export function isOrganizationConversationsKey(
    key: unknown,
    organizationId: string,
): boolean {
    return (
        Array.isArray(key) &&
        key[0] === ORGANIZATION_CONVERSATIONS_SWR_KEY &&
        key[1] === organizationId
    );
}

export async function revalidateOrganizationConversationPages(
    organizationId: string,
): Promise<void> {
    await mutate(
        (key) => isOrganizationConversationsKey(key, organizationId),
        undefined,
        { revalidate: true },
    );
}

export type InboxReadStateConversation = {
    readonly id: string;
    readonly unread_count: number;
};

export type InboxReadStatePage<
    T extends InboxReadStateConversation = InboxReadStateConversation,
> = {
    readonly conversations: ReadonlyArray<T>;
};

export type PersistInboxReadStatesResult = {
    readonly succeededIds: ReadonlyArray<string>;
    readonly failedIds: ReadonlyArray<string>;
};

export function applyConversationUnreadCounts<
    T extends InboxReadStateConversation,
>(
    conversations: ReadonlyArray<T>,
    conversationIds: ReadonlySet<string>,
    unreadCount: 0 | 1,
): Array<T> {
    if (conversationIds.size === 0) {
        return [...conversations];
    }
    return conversations.map((conversation) =>
        conversationIds.has(conversation.id)
            ? { ...conversation, unread_count: unreadCount }
            : conversation,
    );
}

export function applyConversationUnreadCountsToPages<
    T extends InboxReadStateConversation,
    TPage extends InboxReadStatePage<T>,
>(
    pages: ReadonlyArray<TPage> | undefined,
    conversationIds: ReadonlySet<string>,
    unreadCount: 0 | 1,
): Array<TPage> {
    return (pages ?? []).map((page) => ({
        ...page,
        conversations: applyConversationUnreadCounts(
            page.conversations,
            conversationIds,
            unreadCount,
        ),
    }));
}

export async function persistInboxReadStates(
    organizationId: string,
    conversationIds: ReadonlyArray<string>,
    nextUnreadCount: 0 | 1,
): Promise<PersistInboxReadStatesResult> {
    if (conversationIds.length === 0) {
        return { succeededIds: [], failedIds: [] };
    }
    const apiClient = getApiClient();
    const url =
        nextUnreadCount === 0
            ? '/organizations/{organization_id}/conversations/{conversation_id}/mark-read'
            : '/organizations/{organization_id}/conversations/{conversation_id}/mark-unread';
    // Persist one conversation at a time. Concurrent mark-read/unread calls
    // each recompute and SET the shared org unread Redis total, so an older
    // aggregate can overwrite a newer one; sequential posts keep the cache
    // aligned with commits from this bulk action.
    const succeededIds: Array<string> = [];
    const failedIds: Array<string> = [];
    for (const conversationId of conversationIds) {
        try {
            await apiClient.post({
                url,
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                },
            });
            succeededIds.push(conversationId);
        } catch {
            failedIds.push(conversationId);
        }
    }
    return { succeededIds, failedIds };
}
